import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { addMonths } from 'date-fns'
import { useList, useCreate, useNotification } from '@refinedev/core'
import { getTierLabel } from '../utils/tierUtils'

interface Organisation {
  uuid: string
  name: string
  charities_number: string
  has_active_discount: boolean
  tier?: number
  billing_frequency?: string
  billing_amount?: number
}

// Whole calendar months in [applied, expiry); mirrors the backend
// _months_between so the preview matches what gets billed.
function monthsBetween(applied: Date, expiry: Date): number {
  let months =
    (expiry.getFullYear() - applied.getFullYear()) * 12 +
    (expiry.getMonth() - applied.getMonth())
  if (expiry.getDate() < applied.getDate()) months -= 1
  return Math.max(months, 0)
}

interface DiscountCode {
  id: number
  code: string
  description: string
  discount_type: 'percentage'
  discount_value: number
  duration_months: number
  active: boolean
  valid_from: string | null
  valid_until: string | null
  max_uses: number | null
  current_uses: number
}

interface ApplyDiscountModalProps {
  discountCodeID?: number
  organisationUuid?: string
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function ApplyDiscountModal({
  discountCodeID,
  organisationUuid,
  open,
  onClose,
  onSuccess,
}: ApplyDiscountModalProps) {
  const { open: showNotification } = useNotification()
  const [selectedOrg, setSelectedOrg] = useState<Organisation | null>(null)
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<
    number | null
  >(null)
  const [appliedAtDate, setAppliedAtDate] = useState<Date | null>(new Date())
  const [expiryDate, setExpiryDate] = useState<Date | null>(null)
  // Once the admin edits the expiry we stop auto-deriving it from the code.
  const [expiryEdited, setExpiryEdited] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (discountCodeID) {
      setSelectedDiscountCode(discountCodeID)
    }
  }, [discountCodeID])

  // Fetch organisations
  const {
    data: orgsData,
    isLoading: orgsLoading,
    refetch: refetchOrgs,
  } = useList<Organisation>({
    resource: 'discount-codes/orgs',
    pagination: { pageSize: 1000 },
  })

  // Fetch discount codes
  const { data: discountData, isLoading: discountLoading } =
    useList<DiscountCode>({
      resource: 'discount-codes',
      pagination: { pageSize: 1000 },
    })

  const { mutate: applyDiscount } = useCreate()

  const organisations = orgsData?.data || []
  const discountCodes = discountData?.data || []

  // Filter out organisations that already have active discounts
  const availableOrgs = organisations.filter((org) => {
    if (organisationUuid) {
      return org.uuid === organisationUuid && !org.has_active_discount
    }
    return !org.has_active_discount
  })

  // Only offer codes that would actually apply: not archived, within their
  // valid window, and not maxed out. Expired/maxed codes are rejected by the
  // server, so surfacing them here just produces a confusing failed apply.
  const now = Date.now()
  const activeDiscountCodes = discountCodes.filter((code) => {
    if (!code.active) return false
    if (code.valid_from && new Date(code.valid_from).getTime() > now) return false
    if (code.valid_until && new Date(code.valid_until).getTime() < now) return false
    if (code.max_uses != null && code.current_uses >= code.max_uses) return false
    return true
  })

  useEffect(() => {
    if (!organisationUuid || organisations.length === 0) return
    const org = organisations.find((item) => item.uuid === organisationUuid)
    if (org && !org.has_active_discount) {
      setSelectedOrg(org)
    }
  }, [organisationUuid, organisations])

  const handleSubmit = () => {
    if (!selectedOrg || !selectedDiscountCode) {
      showNotification?.({
        type: 'error',
        message: 'Please select both an organisation and discount code',
      })
      return
    }

    setIsSubmitting(true)

    // Use the mutation's onSuccess/onError callbacks rather than awaiting the
    // mutate() call: refine's useCreate().mutate returns void, so awaiting it
    // resolves before the apply persists and any parent refetch would race the
    // server. The callbacks fire only after the request settles.
    applyDiscount(
      {
        resource: 'discount-codes/apply',
        values: {
          org_uuid: selectedOrg.uuid,
          discount_code_id: selectedDiscountCode,
          applied_at: appliedAtDate?.toISOString(),
          expires_at: expiryDate?.toISOString(),
        },
      },
      {
        onSuccess: () => {
          showNotification?.({
            type: 'success',
            message: 'Discount successfully applied to organisation',
          })

          // Reset form
          setSelectedOrg(null)
          setSelectedDiscountCode(null)
          setAppliedAtDate(new Date())
          setExpiryDate(null)
          setExpiryEdited(false)

          // Refresh organisations list to update discount status
          refetchOrgs()

          // Call parent success handler to refresh active discounts
          onSuccess?.()
          setIsSubmitting(false)
          onClose()
        },
        onError: (error: any) => {
          const detail =
            error?.response?.data?.detail ||
            error?.message ||
            'Failed to apply discount. Please try again.'
          showNotification?.({
            type: 'error',
            message: detail,
          })
          setIsSubmitting(false)
        },
      }
    )
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedOrg(null)
      setSelectedDiscountCode(null)
      setAppliedAtDate(new Date())
      setExpiryDate(null)
      setExpiryEdited(false)
      onClose()
    }
  }

  const selectedDiscount = activeDiscountCodes.find(
    (code) => code.id === selectedDiscountCode
  )

  // Default the expiry to applied-at + the code's duration, until the admin
  // overrides it. Re-derives when the code or applied-at changes.
  const selectedDurationMonths = selectedDiscount?.duration_months
  useEffect(() => {
    if (expiryEdited) return
    if (selectedDurationMonths != null && appliedAtDate) {
      setExpiryDate(addMonths(appliedAtDate, selectedDurationMonths))
    } else {
      setExpiryDate(null)
    }
  }, [selectedDurationMonths, appliedAtDate, expiryEdited])

  // Calculate pricing information for selected org
  const pricingInfo = React.useMemo(() => {
    if (!selectedOrg) return null

    // Use custom billing_amount if set, otherwise calculate from tier
    let originalAmount: number
    let monthlyPrice: number

    if (selectedOrg.billing_amount && selectedOrg.billing_amount > 0) {
      originalAmount = selectedOrg.billing_amount

      // Calculate monthly amount based on billing frequency
      const frequency = selectedOrg.billing_frequency || 'Yearly'
      if (frequency === 'Monthly') {
        monthlyPrice = originalAmount
      } else if (frequency === 'Quarterly') {
        monthlyPrice = originalAmount / 3
      } else {
        // Yearly or default
        monthlyPrice = originalAmount / 12
      }
    } else {
      // Default to tier 4 if no tier is set
      const tier = selectedOrg.tier ?? 4

      // Standard tier pricing (monthly rates)
      const tierPricing = {
        0: 20, // Tier Micro
        1: 125, // Large Organisation
        2: 125, // Medium Organisation
        3: 50, // Small Organisation
        4: 35, // Mini Organisation
      }

      monthlyPrice = tierPricing[tier as keyof typeof tierPricing] || 35

      // Calculate amount based on billing frequency
      const frequency = selectedOrg.billing_frequency || 'Yearly'
      if (frequency === 'Monthly') {
        originalAmount = monthlyPrice
      } else if (frequency === 'Quarterly') {
        originalAmount = monthlyPrice * 3
      } else {
        // Yearly or default
        originalAmount = monthlyPrice * 12
      }
    }

    // Calculate discount if discount code is selected
    let discountAmount = 0
    let finalAmount = originalAmount

    if (selectedDiscount) {
      // monthly discount
      const monthlyDiscountedAmount =
        monthlyPrice * (selectedDiscount.discount_value / 100)

      // Prorate by the chosen discount window (applied-at -> expiry), so
      // editing the expiry updates the amount, matching the backend. Fall back
      // to the code's duration before the dates are set.
      const frequency = selectedOrg.billing_frequency || 'Yearly'
      const maxMonths =
        frequency === 'Monthly' ? 1 : frequency === 'Quarterly' ? 3 : 12
      const windowMonths =
        appliedAtDate && expiryDate
          ? monthsBetween(appliedAtDate, expiryDate)
          : selectedDiscount.duration_months || 0
      discountAmount = monthlyDiscountedAmount * Math.min(windowMonths, maxMonths)
      finalAmount = originalAmount - discountAmount
    }

    return {
      tier: selectedOrg.tier ?? 4,
      tierLabel: getTierLabel(selectedOrg.tier ?? 4),
      frequency: selectedOrg.billing_frequency || 'Yearly',
      isCustomBilling: !!(
        selectedOrg.billing_amount && selectedOrg.billing_amount > 0
      ),
      originalAmount,
      discountAmount,
      finalAmount,
    }
  }, [selectedOrg, selectedDiscount, appliedAtDate, expiryDate])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Apply Discount to Organisation</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Organisation Selection */}
          <Autocomplete
            options={availableOrgs}
            getOptionLabel={(option) =>
              `${option.name} (${option.charities_number || 'No reg number'})`
            }
            value={selectedOrg}
            onChange={(_, newValue) => setSelectedOrg(newValue)}
            loading={orgsLoading}
            disabled={isSubmitting || !!organisationUuid}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Organisation"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {orgsLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {availableOrgs.length === 0 && !orgsLoading && (
            <Alert severity="warning">
              All organisations already have active discounts. Remove existing
              discounts first to apply new ones.
            </Alert>
          )}

          {/* Discount Code Selection */}
          <FormControl fullWidth required disabled={isSubmitting}>
            <InputLabel>Discount Code</InputLabel>
            <Select
              disabled={discountCodeID ? true : false}
              value={selectedDiscountCode || ''}
              onChange={(e) => {
                setSelectedDiscountCode(e.target.value as number)
                // New code → re-derive the default expiry from its duration.
                setExpiryEdited(false)
              }}
              label="Discount Code"
            >
              {activeDiscountCodes.map((code) => (
                <MenuItem key={code.id} value={code.id}>
                  <Box>
                    <Typography variant="body1">{code.code}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {code.description} - {code.discount_value}%
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {activeDiscountCodes.length === 0 && !discountLoading && (
            <Alert severity="warning">
              No valid discount codes available (expired and maxed-out codes are
              hidden). Create or renew a discount code first.
            </Alert>
          )}

          {/* Applied At Date Selection */}
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={enGB}
          >
            <DatePicker
              label="Applied At Date"
              value={appliedAtDate}
              onChange={(newValue) => setAppliedAtDate(newValue)}
              disabled={isSubmitting}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  helperText:
                    'Date when the discount is applied to the organisation',
                },
              }}
            />
            <DatePicker
              label="Expiry Date"
              value={expiryDate}
              onChange={(newValue) => {
                setExpiryDate(newValue)
                setExpiryEdited(true)
              }}
              disabled={isSubmitting || !selectedDiscountCode}
              format="dd/MM/yyyy"
              minDate={appliedAtDate ?? undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  sx: { mt: 2 },
                  helperText: selectedDurationMonths
                    ? `Defaults to the code's ${selectedDurationMonths}-month duration; override if needed`
                    : 'When the discount expires',
                },
              }}
            />
          </LocalizationProvider>

          {/* Pricing Information */}
          {pricingInfo && (
            <Box
              sx={{
                p: 3,
                bgcolor: 'primary.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.200',
              }}
            >
              <Typography variant="h6" color="primary.main" gutterBottom>
                Pricing Information for {selectedOrg?.name}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Subscription Tier
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {pricingInfo.tierLabel}{' '}
                    {pricingInfo.isCustomBilling ? '(Custom Billing)' : ''}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Billing Frequency
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {pricingInfo.frequency}
                  </Typography>
                </Box>
              </Box>

              {selectedDiscount && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Discount Preview
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Original Amount
                      </Typography>
                      <Typography variant="h6">
                        ${pricingInfo.originalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Discount ({selectedDiscount.discount_value}%)
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        -${pricingInfo.discountAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Final Amount
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        ${pricingInfo.finalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {!selectedDiscount && (
                <Typography variant="body2" color="text.secondary">
                  Select a discount code above to see the pricing preview.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !selectedOrg ||
            !selectedDiscountCode ||
            isSubmitting ||
            orgsLoading ||
            discountLoading
          }
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Apply Discount'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
